import { useEffect, useRef, useState } from "preact/hooks";
import { previousStep, roleLabel, stepCopy, stepNumber, stepsFor, totalSteps } from "../guide.js";
import type { Audience, DemoRoute, GuideState } from "../types.js";
import { HOME_HREF, HomeLink } from "./home-link.js";

export interface GuideCardProps {
  readonly guide: GuideState;
  readonly audience?: Audience;
  readonly route: DemoRoute;
  readonly whatsapp: string;
  onGo(route: DemoRoute): void;
  onNext(): void;
  onToggle(): void;
  onRestart(): void;
}

export function GuideCard({
  guide,
  audience = "planner",
  route,
  whatsapp,
  onGo,
  onNext,
  onToggle,
  onRestart,
}: GuideCardProps) {
  const [menu, setMenu] = useState(false);
  const holder = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenu(false);
      }
    };
    const onPointer = (event: PointerEvent) => {
      if (!holder.current?.contains(event.target as Node)) {
        setMenu(false);
      }
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [menu]);
  const copy = stepCopy(guide.step, audience);
  const total = totalSteps(audience);
  const before = previousStep(guide.step, audience);
  const finished = route !== copy.route && before?.route === route && before.done ? before : null;
  const number = finished ? stepNumber(finished.step, audience) : stepNumber(guide.step, audience);
  const planner = audience === "planner";
  if (guide.collapsed) {
    return (
      <div class="d-guide is-collapsed" data-theme="light">
        <button type="button" class="d-guide-pill" onClick={onToggle}>
          Guia {number}/{total}
        </button>
      </div>
    );
  }
  const here = route === copy.route;
  const action = !here ? (
    <button type="button" class="d-button d-primary d-small" onClick={() => onGo(copy.route)}>
      {copy.go}
    </button>
  ) : copy.next ? (
    <button type="button" class="d-button d-primary d-small" onClick={onNext}>
      {copy.next}
    </button>
  ) : null;
  return (
    <section
      class="d-guide"
      data-theme="light"
      aria-label="Guia da demonstração"
      data-testid="guide"
    >
      {planner ? (
        <div class="d-guide-home">
          <HomeLink height={18} />
        </div>
      ) : null}
      <div class="d-guide-head">
        <span class="d-guide-role">{roleLabel((finished ?? copy).role, audience)}</span>
        <span class="d-guide-count">
          Passo {number} de {total}
        </span>
      </div>
      <div class="d-guide-holder" ref={holder}>
        <button
          type="button"
          class="d-guide-more"
          aria-label="Mais opções"
          aria-expanded={menu}
          onClick={() => setMenu((open) => !open)}
        >
          ⋯
        </button>
        {menu ? (
          <div class="d-guide-menu" role="menu">
            {planner ? (
              <a class="d-guide-item" role="menuitem" href={whatsapp}>
                Falar no WhatsApp
              </a>
            ) : null}
            {planner ? (
              <a class="d-guide-item" role="menuitem" href={HOME_HREF}>
                Voltar ao site
              </a>
            ) : null}
            <button
              type="button"
              class="d-guide-item"
              role="menuitem"
              onClick={() => {
                setMenu(false);
                onToggle();
              }}
            >
              Recolher guia
            </button>
            <button
              type="button"
              class="d-guide-item"
              role="menuitem"
              onClick={() => {
                setMenu(false);
                onRestart();
              }}
            >
              Recomeçar
            </button>
          </div>
        ) : null}
      </div>
      <p class="d-guide-hint" data-testid="guide-hint">
        {finished?.done ?? copy.hint}
      </p>
      <p class="d-guide-text">{finished?.doneText ?? copy.text}</p>
      {action ? <div class="d-guide-action">{action}</div> : null}
      <div
        class="d-guide-progress"
        role="progressbar"
        aria-label="Progresso da demonstração"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={number}
      >
        <span style={{ width: `${(number / total) * 100}%` }} />
      </div>
      <ol class="d-guide-steps" aria-label="Passos da demonstração">
        {stepsFor(audience)
          .slice(0, total)
          .map((item, index) => {
            const done = finished
              ? index + 1 <= number
              : index + 1 < number || guide.step === "done";
            const current = !finished && item.step === guide.step;
            return (
              <li
                key={item.step}
                class={current ? "is-current" : done ? "is-done" : undefined}
                aria-current={current ? "step" : undefined}
              >
                <span class="d-guide-step-role">{roleLabel(item.role, audience)}</span>
                <span class="d-guide-step-hint">{item.hint}</span>
              </li>
            );
          })}
      </ol>
    </section>
  );
}
